import { CommissionModel, CommissionMutationInput } from 'api-hooks/commission';
import { EnumList, SaleDepartmentEnum } from 'common/constants/enum';
import formSetErrors from 'common/helpers/form-setError';
import notification from 'components/common/notification';
import { Form, FormState, Input } from 'components/elements';
import FormHeader from 'components/widgets/form-header';
import useDestroyAction from 'hooks/use-destroy-action';
import useGetCrudPermission from 'hooks/use-get-crud-permission';
import useYupValidationResolver from 'hooks/use-yup-validation-resolver';
import EnumSelectInput from 'legacy-modules/master/select-input/enum';
import {
  FullContainer,
  HalfContainer,
  SectionContainer,
} from 'legacy-modules/styles';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';
import { useForm } from 'react-hook-form';

import CommissionDetails from './commission-details';
import {
  CommissionFormType,
  CommissionMethodType,
  commissionFormSchema,
} from './form-type';
import useGetDefaultValues from '../hooks/use-get-default-values';

interface Props {
  commission?: CommissionModel;
  onSubmit: (
    input: CommissionMutationInput,
    form: CommissionMethodType,
  ) => Promise<CommissionModel | undefined>;
}

export default function CommissionForm(props: Props) {
  const { t } = useTranslation();
  const { commission } = props;

  const defaultValues = useGetDefaultValues(commission);

  const resolver = useYupValidationResolver(commissionFormSchema());

  const methods = useForm<CommissionFormType>({
    defaultValues,
    resolver,
  });

  const onSubmit = React.useCallback(
    async (values: CommissionFormType) => {
      try {
        const result = {
          ...values,
          commissionMasterAdditionals:
            values.department !== SaleDepartmentEnum.Product
              ? values.commissionMasterAdditionals
              : [],
        };
        await props.onSubmit(result, methods);
      } catch (e) {
        if (e.errors) {
          formSetErrors(e.errors, methods.setError);
        }
        notification.error({ message: e.message });
      }
    },
    [methods, props],
  );

  const customViewName =
    commission &&
    [
      t('common:edit_extra', { extra: t('common:commission') }),
      t(`common:${commission.department}`),
    ].join(' - ');

  const permission = useGetCrudPermission('commission-masters');
  const { onDelete, isLoading: isDeleteLoading } = useDestroyAction(
    'commission-masters',
    commission?.id,
  );
  const { asPath } = useRouter();

  return (
    <Form
      methods={methods}
      onSubmit={onSubmit}
      defaultEditable={!commission}
      customViewName={customViewName}
      keyName={asPath}
    >
      <FormState>
        {({ editable }) => {
          return (
            <>
              <FormHeader
                data={commission}
                onDelete={onDelete}
                isDeleteLoading={isDeleteLoading}
                noDelete={!permission.delete || editable}
                noEdit={!permission.update}
                title={
                  commission
                    ? customViewName ??
                      t('common:edit_extra', { extra: t('common:commission') })
                    : t('common:add_extra', { extra: t('common:commission') })
                }
              />
            </>
          );
        }}
      </FormState>
      <FullContainer>
        <SectionContainer>
          <HalfContainer>
            <Input
              type="date-time"
              name="appliedOn"
              label={t('common:applied_on')}
              placeholder={t('common:applied_on')}
              required
              clearable={false}
            />
          </HalfContainer>
          <HalfContainer>
            <EnumSelectInput
              enumType={EnumList.SaleDepartment}
              name="department"
              label={t('common:department')}
              placeholder={t('common:department')}
              required
              clearable={false}
            />
          </HalfContainer>
        </SectionContainer>
        <CommissionDetails />
      </FullContainer>
    </Form>
  );
}
